import React, { useCallback } from 'react';

import { DatePicker, TimePicker, DateTimePicker } from '@atlaskit/datetime-picker';
import { Box, Text } from '@atlaskit/primitives/compiled';

import { useDataBinding } from '../../hooks/useDataBinding.tsx';
import { useDynamicString } from '../../hooks/useDynamicValue.tsx';
import { useValidation } from '../../hooks/useValidation.tsx';
import { isDataBinding } from '../../types/A2UICommon.types.tsx';
import type { A2UIComponentDefinition } from '../../types/A2UIComponents.types.tsx';
import type { A2UIDateTimeInputProps } from '../../types/A2UIComponents.types.tsx';

type Props = {
	definition: A2UIComponentDefinition;
};

export const A2UIDateTimeInput = ({ definition }: Props): React.JSX.Element => {
	const def = definition as A2UIDateTimeInputProps;
	const label = useDynamicString(def.label);
	const minValue = useDynamicString(def.min);
	const maxValue = useDynamicString(def.max);
	const { isInvalid } = useValidation(def.checks);

	const enableDate = def.enableDate !== false;
	const enableTime = def.enableTime !== false;

	// Two-way binding
	const bindingPath = isDataBinding(def.value) ? def.value.path : null;
	const [boundValue, setBoundValue] = useDataBinding<string>(bindingPath ?? '/___noop');
	const staticValue = useDynamicString(def.value);

	const value = bindingPath ? (boundValue ?? '') : staticValue;

	const handleChange = useCallback(
		(newValue: string) => {
			if (bindingPath) {
				setBoundValue(newValue);
			}
		},
		[bindingPath, setBoundValue],
	);

	const fieldId = `a2ui-datetime-${def.id}`;
	const ariaLabel = def.accessibility?.label ?? label;

	// Date only
	if (enableDate && !enableTime) {
		return (
			<Box>
				{label && (
					<label htmlFor={fieldId}>
						<Text as="span" weight="semibold">
							{label}
						</Text>
					</label>
				)}
				<DatePicker
					id={fieldId}
					value={value}
					onChange={handleChange}
					isInvalid={isInvalid}
					minDate={minValue || undefined}
					maxDate={maxValue || undefined}
					label={ariaLabel}
				/>
			</Box>
		);
	}

	// Time only
	if (!enableDate && enableTime) {
		return (
			<Box>
				{label && (
					<label htmlFor={fieldId}>
						<Text as="span" weight="semibold">
							{label}
						</Text>
					</label>
				)}
				<TimePicker
					id={fieldId}
					value={value}
					onChange={handleChange}
					isInvalid={isInvalid}
					label={ariaLabel}
				/>
			</Box>
		);
	}

	// Date + Time (default)
	return (
		<Box>
			{label && (
				<label htmlFor={fieldId}>
					<Text as="span" weight="semibold">
						{label}
					</Text>
				</label>
			)}
			<DateTimePicker id={fieldId} value={value} onChange={handleChange} isInvalid={isInvalid} />
		</Box>
	);
};
